import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Bell, BellOff, Calendar, Check, Clock, Eye, Loader2,
  MessageSquare, Newspaper, Pencil, Send, Star, Trash2, X,
} from "lucide-react";

import { ArticleFormDialog } from "@/components/ArticleFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useArticleComments,
  useCreateArticleComment,
  useDeleteArticleComment,
  useSubscribeArticle,
  useUnsubscribeArticle,
  useUpdateArticleComment,
} from "@/hooks/useArticleExtras";
import { useDeleteArticle } from "@/hooks/useArticleMutations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useArticle } from "@/hooks/useDetail";
import { formatDateTime, initials } from "@/lib/format";
import type { ArticleCommentRow } from "@/lib/types";
import { cn } from "@/lib/utils";

import { MetaRow, NotFound, WorkflowBadge } from "./DocumentDetail";

/** /articles/:slug — hero image, title, status, rich body, sidebar. */
export default function ArticleDetail() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { data: user } = useCurrentUser();
  const { data: article, isLoading, error } = useArticle(slug);
  const del         = useDeleteArticle();
  const subscribe   = useSubscribeArticle();
  const unsubscribe = useUnsubscribeArticle();
  const confirm     = useConfirm();
  const [editing, setEditing] = useState(false);

  if (isLoading) return <ArticleSkeleton />;
  if (error || !article) return <NotFound back="/articles" label="articles" />;

  const canManage = !!user && (
    user.is_director_level
    || (article.owner_id === user.id && article.status !== "published")
  );

  async function onDelete() {
    if (!article) return;
    const ok = await confirm({
      title: `Delete "${article.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(article.slug, { onSuccess: () => navigate("/articles") });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <ArticleFormDialog open={editing} onOpenChange={setEditing} article={article} />

      {/* ---------- Back link + actions ---------- */}
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/articles">
            <ArrowLeft className="mr-2 h-4 w-4" />Back to articles
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant={article.subscribed ? "secondary" : "outline"}
            size="sm"
            disabled={subscribe.isPending || unsubscribe.isPending}
            onClick={() =>
              article.subscribed
                ? unsubscribe.mutate(article.slug)
                : subscribe.mutate(article.slug)
            }
          >
            {subscribe.isPending || unsubscribe.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : article.subscribed ? (
              <BellOff className="mr-2 h-4 w-4" />
            ) : (
              <Bell className="mr-2 h-4 w-4" />
            )}
            {article.subscribed ? "Unsubscribe" : "Subscribe"}
          </Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />Edit
              </Button>
              <Button
                variant="outline" size="sm"
                className="border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:bg-rose-500/15 hover:text-rose-800 dark:text-rose-300"
                disabled={del.isPending}
                onClick={onDelete}
              >
                {del.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Trash2 className="mr-2 h-4 w-4" />}
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ---------- Hero ---------- */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        {article.image_url ? (
          <div className="relative h-56 w-full overflow-hidden bg-muted sm:h-72">
            <img
              src={article.image_url} alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 space-y-2 p-6 text-white">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-white/30 bg-black/30 text-white">
                  {article.category_display}
                </Badge>
                {article.is_featured && (
                  <Badge variant="outline" className="border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15/95 text-amber-700 dark:text-amber-300">
                    <Star className="mr-1 h-3 w-3 fill-amber-400" />Featured
                  </Badge>
                )}
                <WorkflowBadge state={article.status} label={article.status_display} />
              </div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{article.title}</h1>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-5 p-6 md:p-8">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 shadow-soft">
              <Newspaper className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{article.category_display}</Badge>
                <WorkflowBadge state={article.status} label={article.status_display} />
              </div>
              <h1 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">{article.title}</h1>
            </div>
          </div>
        )}
      </Card>

      {/* ---------- Meta strip ---------- */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        {article.owner_name && (
          <span className="flex items-center gap-1.5">
            By <span className="font-semibold text-foreground">{article.owner_name}</span>
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />{formatDateTime(article.created_at)}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />{article.reading_time} min read
        </span>
        <span className="flex items-center gap-1.5">
          <Eye className="h-3.5 w-3.5" />
          <span className="font-mono tabular-nums">{article.view_count.toLocaleString()}</span> views
        </span>
      </div>

      {/* ---------- Body + sidebar ---------- */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="border-border/60 shadow-soft">
            <CardContent className="prose prose-sm max-w-none p-6 prose-headings:tracking-tight prose-a:text-primary">
              {article.content
                ? <div dangerouslySetInnerHTML={{ __html: article.content }} />
                : <p className="text-muted-foreground">No body content.</p>}
            </CardContent>
          </Card>

          <CommentsSection
            articleId={article.id}
            slug={article.slug}
            initialCount={article.comment_count}
          />
        </div>

        <Card className="h-fit border-border/60 shadow-soft">
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="space-y-3 pt-0 text-sm">
            <MetaRow label="Category" value={article.category_display} />
            <MetaRow label="State"    value={article.status_display} />
            <MetaRow label="Owner"    value={article.owner_name || "—"} />
            <MetaRow label="Views"    value={article.view_count.toLocaleString()} mono />
            <MetaRow label="Reading"  value={`${article.reading_time} min`} mono />
            <MetaRow label="Created"  value={formatDateTime(article.created_at)} />
            <MetaRow label="Updated"  value={formatDateTime(article.updated_at)} />
            <MetaRow label="Slug"     value={<code className="text-xs">{article.slug}</code>} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comments — mirror of the one in DocumentDetail. Different list/mutation
// hooks, same UX (composer + list + edit-in-place + soft-delete).
// ---------------------------------------------------------------------------
function CommentsSection({
  articleId, slug, initialCount,
}: { articleId: string; slug: string; initialCount: number }) {
  const { data: user } = useCurrentUser();
  const list = useArticleComments(articleId);
  const create = useCreateArticleComment();
  const [draft, setDraft] = useState("");

  const visibleCount = list.data ? list.data.count : initialCount;
  const canSubmit = draft.trim().length > 0 && !create.isPending && !!user;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      { article: articleId, slug, body: draft.trim() },
      { onSuccess: () => setDraft("") },
    );
  }

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          Comments
          <Badge variant="outline" className="font-mono tabular-nums">{visibleCount}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {list.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : list.data && list.data.results.length > 0 ? (
          <ul className="space-y-3">
            {list.data.results.map((c) => (
              <li key={c.id}>
                <CommentRow
                  comment={c}
                  slug={slug}
                  currentUserId={user?.id ?? null}
                  canMod={!!user?.is_director_level}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
            No comments yet. Start the conversation.
          </p>
        )}

        {user && (
          <form onSubmit={onSubmit} className="space-y-2 border-t border-border/60 pt-4">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a comment…"
              rows={3}
              maxLength={4000}
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={!canSubmit}>
                {create.isPending
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Posting…</>
                  : <><Send className="mr-2 h-4 w-4" />Comment</>}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function CommentRow({
  comment, slug, currentUserId, canMod,
}: {
  comment: ArticleCommentRow;
  slug: string;
  currentUserId: string | null;
  canMod: boolean;
}) {
  const update  = useUpdateArticleComment();
  const del     = useDeleteArticleComment();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(comment.body);

  const isAuthor = currentUserId != null && comment.author_id === currentUserId;
  const canDel   = isAuthor || canMod;

  function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    update.mutate(
      { id: comment.id, article: comment.article, body: draft.trim() },
      { onSuccess: () => setEditing(false) },
    );
  }

  async function onDelete() {
    const ok = await confirm({
      title: "Delete this comment?",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate({ id: comment.id, article: comment.article, slug });
  }

  return (
    <div id={`comment-${comment.id}`} className="flex items-start gap-3 rounded-md border border-border/60 bg-card p-3">
      <Avatar className="h-8 w-8 ring-1 ring-border">
        {comment.author?.avatar_url && <AvatarImage src={comment.author.avatar_url} alt="" />}
        <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-[11px] font-semibold text-primary-foreground">
          {initials(comment.author_name || "")}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{comment.author_name || "—"}</span>
            {" · "}{formatDateTime(comment.created_at)}
            {comment.edited_at && <span className="ml-1">(edited)</span>}
          </div>
          {!editing && (
            <div className="flex items-center gap-1">
              {comment.can_edit && (
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => { setDraft(comment.body); setEditing(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {canDel && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                  disabled={del.isPending} onClick={onDelete}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <form onSubmit={onSave} className="mt-2 space-y-2">
            <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={4000} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
                <X className="mr-1 h-3.5 w-3.5" />Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!draft.trim() || update.isPending}>
                {update.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Check className="mr-1 h-3.5 w-3.5" />}
                Save
              </Button>
            </div>
          </form>
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{comment.body}</p>
        )}
      </div>
    </div>
  );
}

function ArticleSkeleton() {
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className={cn("w-full", "h-56 sm:h-72")} />
      <Skeleton className="h-6 w-2/3" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-96 w-full lg:col-span-2" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}
